import { useMutation, useQuery } from '@tanstack/react-query';
import { BuildingConfig } from '@cobu/types';
import { useCallback, useEffect } from 'react';
import buildingService from './buildingService';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';

// Polling constants
export const TOOLTIP_POLL_MS = 30_000;

// Query keys
export const BUILDING_KEYS = {
  onboardingConfig: (buildingId: string) => ['building', 'onboardingConfig', buildingId],
  raffle: (buildingId: string) => ['building', 'raffle', buildingId],
  buildingTooltip: (buildingId: string) => ['building', 'tooltip', buildingId],
  buildingConfig: (version: string) => ['building', 'config', version],
};
/**
 * Hook for fetching the building configuration feature flags
 */
interface BuildingConfigQueryOptions {
  enabled?: boolean;
  placeholderData?: BuildingConfig | null;
}

export const useBuildingConfigQuery = (versionNumber?: string, options: BuildingConfigQueryOptions = {}) => {
  // Use VITE_APP_VERSION from .env (same as buildingService)
  const resolvedVersion = versionNumber ?? import.meta.env.VITE_APP_VERSION;

  return useQuery<BuildingConfig | null>({
    queryKey: BUILDING_KEYS.buildingConfig(resolvedVersion || ''),
    queryFn: () => buildingService.getBuildingConfig(resolvedVersion),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: options.enabled ?? true,
    placeholderData: options.placeholderData,
  });
};

/**
 * Hook for fetching onboarding configuration
 */
export const useOnboardingConfigQuery = (buildingId: string) => {
  const query = useQuery({
    queryKey: BUILDING_KEYS.onboardingConfig(buildingId),
    queryFn: () => buildingService.getOnboardingConfig(buildingId),
    enabled: !!buildingId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Handle success and error analytics
  useEffect(() => {
    if (query.data && !query.isLoading) {
      analyticsService.trackEvent(AnalyticsEvent.UPDATE_USER_SUCCESS, {
        action: 'onboarding_config_loaded',
        buildingId,
        hasConfig: query.data && query.data.length > 0,
      });
    }
  }, [query.data, query.isLoading, buildingId]);

  useEffect(() => {
    if (query.error) {
      console.error('Failed to load onboarding config:', query.error);
      analyticsService.trackEvent(AnalyticsEvent.UPDATE_USER_FAILURE, {
        action: 'onboarding_config_load_failed',
        buildingId,
        error: query.error.message,
      });
    }
  }, [query.error, buildingId]);

  return query;
};

/**
 * Hook for fetching raffle data
 */
export const useRaffleQuery = (buildingId: string) => {
  const query = useQuery({
    queryKey: BUILDING_KEYS.raffle(buildingId),
    queryFn: () => buildingService.getRaffle(buildingId),
    enabled: !!buildingId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Handle success and error analytics
  useEffect(() => {
    if (query.data && !query.isLoading) {
      analyticsService.trackEvent(AnalyticsEvent.UPDATE_USER_SUCCESS, {
        action: 'raffle_data_loaded',
        buildingId,
        hasRaffle: query.data && query.data.length > 0,
      });
    }
  }, [query.data, query.isLoading, buildingId]);

  useEffect(() => {
    if (query.error) {
      console.error('Failed to load raffle data:', query.error);
      analyticsService.trackEvent(AnalyticsEvent.UPDATE_USER_FAILURE, {
        action: 'raffle_data_load_failed',
        buildingId,
        error: query.error.message,
      });
    }
  }, [query.error, buildingId]);

  return query;
};

/**
 * Hook for fetching building tooltip
 */
interface TooltipQueryOptions {
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useGetBuildingTooltipQuery = (buildingId: string, options: TooltipQueryOptions = {}) => {
  const { enabled = true, refetchInterval } = options;
  const query = useQuery({
    queryKey: BUILDING_KEYS.buildingTooltip(buildingId),
    queryFn: () => buildingService.getBuildingTooltip(buildingId),
    enabled: !!buildingId && enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 30 * 1000, // 30 seconds
    refetchInterval,
  });

  // Handle success and error analytics
  useEffect(() => {
    if (query.data && !query.isLoading) {
      /* ANALYTICS_DISABLED: BUILDING_TOOLTIP_LOADED ('building_tooltip_loaded')
        analyticsService.trackEvent(AnalyticsEvent.BUILDING_TOOLTIP_LOADED, {
                buildingId,
                hasTooltip: query.data && query.data.length > 0,
              });
        */
    }
  }, [query.data, query.isLoading, buildingId]);

  useEffect(() => {
    if (query.error) {
      console.error('Failed to load building tooltip:', query.error);
      /* ANALYTICS_DISABLED: BUILDING_TOOLTIP_LOAD_FAILED ('building_tooltip_load_failed')
        analyticsService.trackEvent(AnalyticsEvent.BUILDING_TOOLTIP_LOAD_FAILED, {
                buildingId,
                error: query.error.message,
              });
        */
    }
  }, [query.error, buildingId]);

  return query;
};

/**
 * Mutation: complete building survey
 */
export const useCompleteBuildingSurveyMutation = () => {
  return useMutation({
    mutationFn: async (surveyId: string) => {
      await buildingService.completeBuildingSurvey(surveyId);
    },
  });
};

/**
 * Mutation: complete building tooltip survey
 */
export const useCompleteBuildingTooltipSurveyMutation = () => {
  return useMutation({
    mutationFn: async (surveyId: string) => {
      await buildingService.completeBuildingTooltipSurvey(surveyId);
    },
  });
};

/**
 * Comprehensive building hook combining all building functionality
 */
export const useBuilding = (buildingId: string) => {
  const onboardingConfigQuery = useOnboardingConfigQuery(buildingId);
  const raffleQuery = useRaffleQuery(buildingId);

  const refetchAll = async () => {
    await Promise.all([
      onboardingConfigQuery.refetch(),
      raffleQuery.refetch(),
    ]);
  };

  return {
    // Onboarding config
    onboardingConfig: onboardingConfigQuery.data?.[0] || null,
    isOnboardingConfigLoading: onboardingConfigQuery.isLoading,
    isOnboardingConfigError: onboardingConfigQuery.isError,
    onboardingConfigError: onboardingConfigQuery.error,

    // Raffle data
    raffle: raffleQuery.data?.[0] || null,
    isRaffleLoading: raffleQuery.isLoading,
    isRaffleError: raffleQuery.isError,
    raffleError: raffleQuery.error,

    // Combined states
    isLoading: onboardingConfigQuery.isLoading || raffleQuery.isLoading,
    isError: onboardingConfigQuery.isError || raffleQuery.isError,
    error: onboardingConfigQuery.error || raffleQuery.error,

    // Actions
    refetchAll,
    refetchOnboardingConfig: onboardingConfigQuery.refetch,
    refetchRaffle: raffleQuery.refetch,
  };
}; 